import * as React from 'react';
import { connect } from 'react-redux';
import { provideLocalizationService, registerForLocalization } from '@progress/kendo-react-intl';
import { Loader } from '@progress/kendo-react-indicators';
import { QRCodeSVG } from 'qrcode.react';
import styled from 'styled-components';
import { withRouter } from '../../../components/withRouter';
import { loadSessionQrDisplay, setSessionQrDisplayDisabled } from '../actions/session-qr-display-page-actions';
import {
  QR_BROADCAST_CHANNEL,
  QR_BROADCAST_ROTATE,
  QR_BROADCAST_DISABLE,
  QR_BROADCAST_CLOSE,
  CountdownText,
  computeCountdown
} from './session-qr-shared';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: #ffffff;
  padding: 24px;
  overflow: hidden;
  box-sizing: border-box;
`;

const DisabledMessage = styled.div`
  font-size: 20px;
  color: #666;
  text-align: center;
`;

class SessionQrDisplayPage extends React.PureComponent {

  constructor() {
    super();
    this.state = { countdown: null };
    this.countdownInterval = null;
    this.retryTimer = null;
    this.broadcastChannel = null;
    this.handleBroadcastMessage = this.handleBroadcastMessage.bind(this);
  }

  componentDidMount() {
    const eventId = this.props.params?.generalCompetencyEventId;
    const sessionId = this.props.params?.sessionId;
    if (eventId && sessionId) {
      this.props.loadSessionQrDisplay({ eventId, sessionId });
    }
    this.broadcastChannel = new BroadcastChannel(QR_BROADCAST_CHANNEL);
    this.broadcastChannel.onmessage = this.handleBroadcastMessage;
  }

  componentDidUpdate(prevProps) {
    if (this.props.qr && this.props.qr !== prevProps.qr) {
      this.startCountdown();
    }
    if (!this.props.qr && prevProps.qr && !this.props.disabled) {
      this.stopCountdown();
    }
  }

  componentWillUnmount() {
    this.stopCountdown();
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
  }

  render() {
    const localizationService = provideLocalizationService(this);
    const { loading, qr } = this.props;
    const qrSize = Math.min(window.innerHeight - 100, window.innerWidth - 200);

    return (
      <Container>
        {loading && !qr && (
          <Loader size='large' />
        )}
        {this.props.disabled && (
          <DisabledMessage>
            {localizationService.toLanguageString('generalCompetencyEventSessionQr.qrDisabled')}
          </DisabledMessage>
        )}
        {!this.props.disabled && qr && (
          <>
            <QRCodeSVG value={qr.url} size={qrSize} />
            {this.state.countdown && (
              <CountdownText $expired={this.state.countdown.expired}>
                {this.state.countdown.expired
                  ? localizationService.toLanguageString('generalCompetencyEventSessionQr.expired')
                  : `${localizationService.toLanguageString('generalCompetencyEventSessionQr.expiresIn')} ${this.state.countdown.text}`
                }
              </CountdownText>
            )}
          </>
        )}
        {!this.props.disabled && !loading && !qr && (
          <DisabledMessage>
            {localizationService.toLanguageString('generalCompetencyEventSessionQr.noActiveQr')}
          </DisabledMessage>
        )}
      </Container>
    );
  }

  startCountdown() {
    this.stopCountdown();
    this.updateCountdown();
    this.countdownInterval = setInterval(() => this.updateCountdown(), 1000);
  }

  stopCountdown() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    this.setState({ countdown: null });
  }

  updateCountdown() {
    const countdown = computeCountdown(this.props.qr?.validUntil);
    if (!countdown) {
      return;
    }
    this.setState({ countdown });
    if (countdown.expired) {
      this.stopCountdown();
      this.scheduleReload();
    }
  }

  scheduleReload() {
    const eventId = this.props.params?.generalCompetencyEventId;
    const sessionId = this.props.params?.sessionId;
    this.retryTimer = setTimeout(() => {
      this.props.loadSessionQrDisplay({ eventId, sessionId });
    }, 2000);
  }

  handleBroadcastMessage(event) {
    const eventId = this.props.params?.generalCompetencyEventId;
    const sessionId = this.props.params?.sessionId;
    switch (event.data?.type) {
      case QR_BROADCAST_ROTATE:
        if (this.retryTimer) {
          clearTimeout(this.retryTimer);
          this.retryTimer = null;
        }
        this.props.setSessionQrDisplayDisabled(false);
        this.props.loadSessionQrDisplay({ eventId, sessionId });
        break;
      case QR_BROADCAST_DISABLE:
        this.stopCountdown();
        if (this.retryTimer) {
          clearTimeout(this.retryTimer);
          this.retryTimer = null;
        }
        this.props.setSessionQrDisplayDisabled(true);
        break;
      case QR_BROADCAST_CLOSE:
        window.close();
        break;
      default:
        break;
    }
  }
}

registerForLocalization(SessionQrDisplayPage);

const mapStateToProps = state => ({
  loading: state.sessionQrDisplayPage.loading,
  qr: state.sessionQrDisplayPage.qr,
  disabled: state.sessionQrDisplayPage.disabled
});

const mapDispatchToProps = dispatch => ({
  loadSessionQrDisplay: (payload) => dispatch(loadSessionQrDisplay(payload)),
  setSessionQrDisplayDisabled: (disabled) => dispatch(setSessionQrDisplayDisabled(disabled))
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SessionQrDisplayPage));
